/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { Fragment, ReactNode } from 'react';

import { Modal } from '@zapier/common-components';
import { isolateComponent, responsiveStylesFn } from '@zapier/common-utils';
import { Colors, Heading, Spacer, Text } from '@zapier/design-system';
import { Spacing } from '@zapier/theme';

import { Currency, PlanV2 } from '@zapier/design-system-revenue/src/types';
import { useSize } from '@zapier/design-system-revenue/src/utils';

import { LegacyPlanCard } from './LegacyPlanCard';

type Props = {
  currency: Currency;
  onClose: (...args: any[]) => void;
  plan: PlanV2;
};

const rootStyles = responsiveStylesFn({
  small: css`
    align-items: center;
    display: flex;
    flex-direction: column-reverse;
  `,
  medium: css`
    flex-direction: row;
  `,
});

const faqStyles = responsiveStylesFn({
  small: css`
    margin-top: ${`${Spacing.large}px`};
    max-width: 250px;
    padding-right: 0;
  `,
  medium: css`
    margin-top: 0;
    max-width: 100%;
    padding-right: ${`${Spacing.large}px`};
  `,
  large: css`
    margin-top: 0;
  `,
});

const legacyPlanCardStyles = responsiveStylesFn({
  small: css`
    border-left: 0;
    padding: 0;
  `,
  medium: css`
    border-left: 1px solid ${Colors.grey200};
    padding: ${`0 ${Spacing.large}px`};
  `,
});

type FaqProps = {
  children: ReactNode;
  title: string;
};

const Faq = ({ children, title }: FaqProps) => (
  <Fragment>
    <Text color="grey900" type="bodyText800">
      <strong>{title}</strong>
    </Text>

    <Spacer height={Spacing.small} />

    <p>
      <Text color="grey700" type="bodyText800">
        {children}
      </Text>
    </p>
  </Fragment>
);

/**
 * A modal that shows the user their current legacy plan, along with some
 * frequently asked questions about legacy plans and pricing.
 */
export const LegacyPlanModal = ({ currency, onClose, plan }: Props) => {
  const size = useSize();

  return (
    <Modal fullStoryEventName="Viewed Legacy Plan Modal" onClose={onClose}>
      <div css={rootStyles(size)}>
        <div css={faqStyles(size)}>
          <Heading>
            <Text color="grey700" type="allCaps800">
              Frequently Asked Questions
            </Text>
          </Heading>

          <Spacer height={Spacing.medium} />

          <Faq title="Can I keep my current plan?">
            Yes. Even as Zapier prices have changed, your current plan is
            unaffected. You can stay on your current plan and we will not change
            your pricing.
          </Faq>

          <Spacer height={size === 'small' ? Spacing.extraLarge : 20} />

          <Faq title="Am I eligible for the pricing I saw previously?">
            We no longer offer those previous plans. To upgrade or change your
            plan, please see our current plans and pricing.
          </Faq>
        </div>

        <div css={legacyPlanCardStyles(size)}>
          <LegacyPlanCard currency={currency} plan={plan} size={size} />
        </div>
      </div>
    </Modal>
  );
};
